import { z } from "zod";
import {
  paginatedSchema,
  paginationQuerySchema,
  type PaginationQueryInput,
} from "../common/pagination.js";
import {
  emailSchema,
  httpUrlSchema,
  idSchema,
  isoDateTimeSchema,
  slugSchema,
  timezoneSchema,
  tokenSchema,
  userSummarySchema,
} from "../common/primitives.js";
import { assignableRoleSchema, workspaceRoleSchema, type WorkspaceRole } from "./roles.js";

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const workspaceSchema = z.object({
  id: idSchema,
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  logoUrl: z.string().nullable(),
  ownerId: idSchema,
  timezone: z.string(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type Workspace = z.infer<typeof workspaceSchema>;

/** A workspace as seen by the current user, including their role in it. */
export const workspaceWithRoleSchema = workspaceSchema.extend({
  role: workspaceRoleSchema,
});
export type WorkspaceWithRole = z.infer<typeof workspaceWithRoleSchema>;

export const workspaceMemberSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  userId: idSchema,
  role: workspaceRoleSchema,
  joinedAt: isoDateTimeSchema,
  user: userSummarySchema,
});
export type WorkspaceMember = z.infer<typeof workspaceMemberSchema>;

export const workspaceInvitationSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  email: z.string(),
  role: workspaceRoleSchema,
  invitedBy: userSummarySchema.nullable(),
  expiresAt: isoDateTimeSchema,
  createdAt: isoDateTimeSchema,
});
export type WorkspaceInvitation = z.infer<typeof workspaceInvitationSchema>;

export const invitationPreviewSchema = z.object({
  workspace: z.object({
    id: idSchema,
    name: z.string(),
    slug: z.string(),
    logoUrl: z.string().nullable(),
  }),
  email: z.string(),
  role: workspaceRoleSchema,
  invitedByName: z.string().nullable(),
  expiresAt: isoDateTimeSchema,
});
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;

/** A pending invitation addressed to the signed-in user (listed without the emailed link). */
export const userInvitationSchema = invitationPreviewSchema.extend({ id: idSchema });
export type UserInvitation = z.infer<typeof userInvitationSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

const workspaceNameSchema = z
  .string()
  .trim()
  .min(1, "Name is required")
  .max(80, "Name must be at most 80 characters");
const workspaceDescriptionSchema = z
  .string()
  .trim()
  .max(500, "Description must be at most 500 characters");

export const createWorkspaceSchema = z.object({
  name: workspaceNameSchema,
  slug: slugSchema,
  description: workspaceDescriptionSchema.optional(),
  timezone: timezoneSchema.default("UTC"),
});
export type CreateWorkspaceInput = z.infer<typeof createWorkspaceSchema>;
export type CreateWorkspaceRequest = z.input<typeof createWorkspaceSchema>;

export const updateWorkspaceSchema = z
  .object({
    name: workspaceNameSchema.optional(),
    slug: slugSchema.optional(),
    description: workspaceDescriptionSchema.nullable().optional(),
    logoUrl: httpUrlSchema.nullable().optional(),
    timezone: timezoneSchema.optional(),
  })
  .refine((value) => Object.values(value).some((v) => v !== undefined), {
    error: "Provide at least one field to update",
  });
export type UpdateWorkspaceInput = z.infer<typeof updateWorkspaceSchema>;

export const transferOwnershipSchema = z.object({ userId: idSchema });
export type TransferOwnershipInput = z.infer<typeof transferOwnershipSchema>;

export const listMembersQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(100).optional(),
  role: workspaceRoleSchema.optional(),
});
export type ListMembersQuery = z.infer<typeof listMembersQuerySchema>;
export interface ListMembersQueryInput extends PaginationQueryInput {
  search?: string | undefined;
  role?: WorkspaceRole | undefined;
}

export const updateMemberRoleSchema = z.object({ role: assignableRoleSchema });
export type UpdateMemberRoleInput = z.infer<typeof updateMemberRoleSchema>;

export const createInvitationSchema = z.object({
  email: emailSchema,
  role: assignableRoleSchema,
});
export type CreateInvitationInput = z.infer<typeof createInvitationSchema>;

export const workspaceParamsSchema = z.object({ workspaceId: idSchema });
export const memberParamsSchema = workspaceParamsSchema.extend({ userId: idSchema });
export const invitationParamsSchema = workspaceParamsSchema.extend({ invitationId: idSchema });
export const invitationTokenParamsSchema = z.object({ token: tokenSchema });
export const userInvitationParamsSchema = z.object({ invitationId: idSchema });

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export const workspacesResponseSchema = z.object({ workspaces: z.array(workspaceWithRoleSchema) });
export type WorkspacesResponse = z.infer<typeof workspacesResponseSchema>;

export const workspaceResponseSchema = z.object({ workspace: workspaceWithRoleSchema });
export type WorkspaceResponse = z.infer<typeof workspaceResponseSchema>;

export const membersResponseSchema = paginatedSchema(workspaceMemberSchema);
export type MembersResponse = z.infer<typeof membersResponseSchema>;

export const memberResponseSchema = z.object({ member: workspaceMemberSchema });
export type MemberResponse = z.infer<typeof memberResponseSchema>;

export const invitationsResponseSchema = z.object({
  invitations: z.array(workspaceInvitationSchema),
});
export type InvitationsResponse = z.infer<typeof invitationsResponseSchema>;

export const invitationResponseSchema = z.object({ invitation: workspaceInvitationSchema });
export type InvitationResponse = z.infer<typeof invitationResponseSchema>;

/**
 * Creating an invitation also returns a shareable link the inviter can copy. It is separate from
 * the emailed link and does not verify the invitee’s email when used.
 */
export const createInvitationResponseSchema = invitationResponseSchema.extend({
  inviteUrl: z.url(),
});
export type CreateInvitationResponse = z.infer<typeof createInvitationResponseSchema>;

export const invitationLinkResponseSchema = z.object({ inviteUrl: z.url() });
export type InvitationLinkResponse = z.infer<typeof invitationLinkResponseSchema>;

export const invitationPreviewResponseSchema = z.object({ invitation: invitationPreviewSchema });
export type InvitationPreviewResponse = z.infer<typeof invitationPreviewResponseSchema>;

export const userInvitationsResponseSchema = z.object({
  invitations: z.array(userInvitationSchema),
});
export type UserInvitationsResponse = z.infer<typeof userInvitationsResponseSchema>;
